import { createContext, useContext, useMemo, useRef, ReactNode } from "react";

export interface PortalContainer {
  [portalId: string]: HTMLElement;
}

export interface PortalContextValue {
  /** Creates a new portal container element for the specified portal ID */
  createPortal: (portalId: string) => HTMLElement;
  /** Removes a portal container and cleans up its references */
  removePortal: (portalId: string) => void;
  /** Gets the container element for a specific portal ID */
  getPortalContainer: (portalId: string) => HTMLElement | undefined;
  /** Subscribes to portal events for inter-component communication */
  subscribe: (handler: (event: any) => void) => void;
  /** Unsubscribes from portal events */
  unsubscribe: (handler: (event: any) => void) => void;
  /** Emits an event to all subscribed handlers */
  emit: (event: any) => void;
  /** Map of all active portal containers */
  containers: PortalContainer;
}

const PortalContext = createContext<PortalContextValue | null>(null);

export interface PortalProviderProps {
  children: ReactNode;
}

/**
 * PortalProvider establishes the portal context and manages the lifecycle of portal containers.
 *
 * This component creates and maintains a registry of DOM elements that serve as portal containers,
 * provides methods for creating and destroying portals dynamically, and facilitates event-based
 * communication between components that use portals. It should wrap any part of the component
 * tree that needs portal functionality.
 *
 * The provider maintains a map of portal containers and ensures proper cleanup when portals
 * are no longer needed. It also provides an event system that allows components to communicate
 * across portal boundaries without tight coupling.
 */
export function PortalProvider({ children }: PortalProviderProps) {
  const containersRef = useRef<PortalContainer>({});
  const eventHandlersRef = useRef<((event: any) => void)[]>([]);

  const contextValue = useMemo(
    (): PortalContextValue => ({
      createPortal: (portalId: string) => {
        if (containersRef.current[portalId]) {
          return containersRef.current[portalId];
        }

        const element = document.createElement("div");
        element.className =
          "portal [.pss-portal-fill-width_&]:w-full [.pss-portal-fill-height_&]:h-full [.pss-portal-content-width_&]:w-fit [.pss-portal-content-height_&]:h-auto";
        element.setAttribute("data-portal-id", portalId);
        containersRef.current[portalId] = element;

        return element;
      },

      removePortal: (portalId: string) => {
        const container = containersRef.current[portalId];
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
        delete containersRef.current[portalId];
      },

      getPortalContainer: (portalId: string) => {
        return containersRef.current[portalId];
      },

      subscribe: (handler: (event: any) => void) => {
        eventHandlersRef.current.push(handler);
      },

      unsubscribe: (handler: (event: any) => void) => {
        const index = eventHandlersRef.current.indexOf(handler);
        if (index > -1) {
          eventHandlersRef.current.splice(index, 1);
        }
      },

      emit: (event: any) => {
        eventHandlersRef.current.forEach((handler) => handler(event));
      },

      containers: containersRef.current,
    }),
    []
  );

  return (
    <PortalContext.Provider value={contextValue}>
      {children}
    </PortalContext.Provider>
  );
}

PortalProvider.displayName = "Portal.Provider";

/**
 * Hook to access the portal context and its methods.
 *
 * Provides access to portal creation, destruction, and event communication methods.
 * Must be used within a PortalProvider component tree. Enables components to create
 * portals dynamically, retrieve portal containers, and participate in the portal
 * event system for cross-component communication.
 */
export function usePortalContext(): PortalContextValue {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error("usePortalContext must be used within a PortalProvider");
  }
  return context;
}
